import React, { useState, useEffect } from 'react';
import api from './api/axiosInstance';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Row, Col, Container, Alert, Form, Button, Modal, Image, Table, Pagination} from 'react-bootstrap';
import CountryAddGeneral from './CountryAddGeneral';
import CountryGeneralInfo from './CountryGeneralInfo.js';
import NavTabs from './NavTabs';
import Breadcrumb from './Breadcrumb';
import Sidebar from './Sidebar.js';
import { validateURL } from './utils/validation';
import { TicketCountContext } from './TicketCountContext';
import icSucess from './assets/img/ic-celebration-circle.svg';
import CityFilter from './CityFilter';
import { exportCountryPdf } from './api/exportCountryPdf';

const CountryEditGeneral = () => {
    const { id } = useParams(); // country _id from URL
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        country: { id: '', name: '' },
        url: '',
        citizensNumber: '',
        languagesSpoken: '',
        currency: '',
        continent: '',
    });

    const errors = {}
    const [loading, setLoading] = useState(true); // ✅ Show spinner while fetching

    const [showErrorMessage, setShowErrorMessage] = useState(false);
    
    const { ticketCount } = React.useContext(TicketCountContext);

    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(true);
    const [citiesError, setCitiesError] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');

    const [citiesPerPage, setCitiesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const normalizedQuery = searchQuery.trim().toLowerCase();

    const filteredCities = !normalizedQuery
        ? cities
        : cities.filter((city) => {
            const name = (city.name || '').toLowerCase();
            const id = (city.cityId || '').toLowerCase();
            const comments = String(city.comments ?? '').toLowerCase();
            return (
                name.includes(normalizedQuery) ||
                id.includes(normalizedQuery) ||
                comments.includes(normalizedQuery)
            );
        });

    // ✅ pagination for cities
    const totalPages = Math.ceil(filteredCities.length / citiesPerPage) || 1;
    const indexOfLastCity = currentPage * citiesPerPage;
    const indexOfFirstCity = indexOfLastCity - citiesPerPage;
    const currentCities = filteredCities.slice(indexOfFirstCity, indexOfLastCity);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, citiesPerPage]);

    const countryNavItems = [
        { to: '/country-edit-general/:id', eventKey: 'countryInfo', label: 'General' },
        { to: '/country-edit-visa-stay/:id', eventKey: 'visaStay', label: 'Visa & Stay' },
        { to: '/country-edit-work-tax/:id', eventKey: 'countryTax', label: 'Work & Tax' },
        { to: '/country-edit-financials-banks/:id', eventKey: 'financialsBanks', label: 'Financials & Banks' },
        { to: '/country-edit-housing-life/:id', eventKey: 'housingLife', label: 'Housing & Life' },
        { to: '/country-edit-social/:id', eventKey: 'social', label: 'Social' },
        { to: '/country-edit-industries/:id', eventKey: 'industries', label: 'Industries & Carrier' },
    ];

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSavedModal, setShowSavedModal] = useState(false);

    const handleExportPdf = async () => {
        try {
            await exportCountryPdf(id);
        } catch (error) {
            console.error('Error exporting country PDF:', error);
            alert('An error occurred while exporting the country report.');
        }
    };

    const handleDeleteCountry = () => {
        setShowDeleteModal(true);
    };

    const confirmDeleteCountry = async () => {
        try {
            await api.delete(`/api/countries/${id}`)
            setShowDeleteModal(false);
            navigate('/onboarding-country-list'); // Redirect to company list after deletion
        } catch (error) {
            console.error('Error deleting country:', error);
            alert('An error occurred while deleting the country.');
        }
    };

    const handleEditClick = () => {
        navigate('/onboarding-city-list');
    };

    useEffect(() => {
        const fetchCountry = async () => {
            try {
                const response = await api.get(`/api/countries/${id}`);

                const data = response.data;

                // ✅ Ensure fallback/defaults are safe
                setFormData({
                    country: {
                        id: data.country?.id || '',
                        name: data.country?.name || ''
                    },
                    url: data.url || '',
                    citizensNumber: data.citizensNumber || '',
                    languagesSpoken: Array.isArray(data.languagesSpoken) ? data.languagesSpoken : [],
                    currency: data.currency || '',
                    continent: data.continent || ''
                });

                setLoading(false); // ✅ Done loading
            } catch (error) {
                console.error('Error fetching country:', error);
                setLoading(false); // ✅ Avoid infinite spinner
            }
        };

        fetchCountry();
    }, [id]);
    
    // fetch cities for this country
    useEffect(() => {
        const fetchCities = async () => {
            try {
                setCitiesLoading(true);
                const res = await api.get(
                    `/api/countries/${id}/cities`,
                );
                setCities(res.data || []);
                setCitiesError(null);
            } catch (error) {
                console.error('Error fetching cities for country:', error);
                setCitiesError('Error fetching cities for this country.');
            } finally {
                setCitiesLoading(false);
            }
        };

        fetchCities();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        console.log('handleChange called with:', name, value);

        // Check if the incoming change is for the country object and handle accordingly
        if (name === 'country' && typeof value === 'object') {
            setFormData(prevData => ({
                ...prevData,
                country: { ...value }  // Update the country object properly
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value  // This assumes value is always correct
            }));
        }
    };

    const handleGeneralSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                country: formData.country,
                url: formData.url,
                citizensNumber: formData.citizensNumber,
                languagesSpoken: Array.isArray(formData.languagesSpoken) ? formData.languagesSpoken : [],
                currency: formData.currency,
                continent: formData.continent,
            };

            const response = await api.patch(`/api/countries/${id}`, payload);
            console.log('Patched country (general):', response.data);
            setShowSavedModal(true);
        } catch (error) {
            console.error('Error updating country:', error);
        }
    };

    // ✅ Toggle Activity status for a city in this country
    const toggleActivityStatus = async (cityId) => {
        try {
            const response = await api.patch(
                `/api/cities/toggle-status/${cityId}`,
                {},
            );

            const updatedStatus = response.data.activityStatus;
            console.log(`City ID: ${cityId}, Updated Status: ${updatedStatus}`);

            // ✅ Update local cities list
            setCities(prev =>
                prev.map(city =>
                    city._id === cityId ? { ...city, activityStatus: updatedStatus } : city
                )
            );
        } catch (error) {
            console.error('Error toggling activity status:', error);
        }
    };

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <CountryGeneralInfo
                                                            formData={formData}
                                                            liveCitiesCount={cities.length}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={countryNavItems} />
                                                    </Col>
                                                </Row>
                                                <Form onSubmit={handleGeneralSubmit}>
                                                    <Row>
                                                        <Col>
                                                            {loading ? (
                                                                <div>Loading...</div>
                                                            ) : (
                                                                <CountryAddGeneral
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                    validateURL={validateURL}
                                                                    isEditPage={true}
                                                                    handleGeneralSubmit={handleGeneralSubmit}
                                                                />
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </Form>

                                                {/* ✅ Cities belonging to this country */}
                                                <Card className="mb-4">
                                                    <Card.Body>
                                                        {/* Title and Edit Button */}
                                                        <Row className="align-items-center">
                                                            <Col>
                                                                <div className="float-end">
                                                                    <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                                                </div>
                                                                <h3><span className="item-number">2.</span> List of cities</h3>
                                                                <hr />
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col xs={12}>
                                                                <CityFilter
                                                                    searchQuery={searchQuery}
                                                                    setSearchQuery={setSearchQuery}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <h3 className="h5 mb-3">Cities in this country</h3>

                                                                {citiesLoading ? (
                                                                    <div>Loading cities...</div>
                                                                ) : citiesError ? (
                                                                    <Alert variant="danger">{citiesError}</Alert>
                                                                ) : cities.length === 0 ? (
                                                                    <p className="text-muted">No cities have been added for this country yet.</p>
                                                                ) : (
                                                                    <>
                                                                        <div className="table-responsive list">
                                                                            <Table>
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th className="th-fixed-city-id">ID</th>
                                                                                        <th className="th-fixed-city-name">City Region</th>
                                                                                        <th className="th-fixed-city-country">Country</th>
                                                                                        <th className="th-fixed-city-comments">Comments</th>
                                                                                        <th className="th-fixed-city-status">Active</th>
                                                                                        <th></th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {currentCities.map((city) => (
                                                                                        <tr key={city._id} style={{ cursor: 'pointer' }}>
                                                                                            <td>{city.cityId}</td>
                                                                                            <td>{city.name}</td>
                                                                                            <td>{city.country?.name || '-'}</td>
                                                                                            <td>{city.comments || '-'}</td>
                                                                                            <td>
                                                                                                <div className="form-check form-switch">
                                                                                                    <input
                                                                                                        className="form-check-input"
                                                                                                        type="checkbox"
                                                                                                        role="switch"
                                                                                                        id={`status-switch-${city._id}`}
                                                                                                        checked={!!city.activityStatus}
                                                                                                        onChange={() => toggleActivityStatus(city._id)}
                                                                                                    />
                                                                                                </div>
                                                                                            </td>
                                                                                            <td className="text-end">
                                                                                                <Link
                                                                                                    to={`/city-edit-general/${city._id}`}
                                                                                                    className="btn btn-sm btn-outline-secondary"
                                                                                                >
                                                                                                    Edit
                                                                                                </Link>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>

                                                                        {/* Pagination Row with Total Count, Items Per Page Selector & "Go to Page" Input */}
                                                                        <Row className="pagination-wrapper mt-3">
                                                                            <Col className="d-flex align-items-center">
                                                                                <span>Total&nbsp;{filteredCities.length}&nbsp;Cities</span>
                                                                                <Pagination>
                                                                                    <Pagination.Prev
                                                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                                        disabled={currentPage === 1}
                                                                                    />
                                                                                    {[...Array(totalPages).keys()].map(number => (
                                                                                        <Pagination.Item
                                                                                            key={number + 1}
                                                                                            active={number + 1 === currentPage}
                                                                                            onClick={() => handlePageChange(number + 1)}
                                                                                        >
                                                                                            {number + 1}
                                                                                        </Pagination.Item>
                                                                                    ))}
                                                                                    <Pagination.Next
                                                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                                                        disabled={currentPage === totalPages}
                                                                                    />
                                                                                </Pagination>
                                                                            </Col>
                                                                            <Col className="pagination-controls">
                                                                                <div className="pagination-controls-per-page">
                                                                                    <Form.Select
                                                                                        value={citiesPerPage}
                                                                                        onChange={(e) => setCitiesPerPage(Number(e.target.value))}
                                                                                        style={{ width: 'auto', marginRight: '10px' }}
                                                                                    >
                                                                                        <option value="10">10 / page</option>
                                                                                        <option value="20">20 / page</option>
                                                                                        <option value="40">40 / page</option>
                                                                                    </Form.Select>
                                                                                </div>
                                                                                <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                                                    Go to
                                                                                </label>
                                                                                <Form.Control
                                                                                    type="number"
                                                                                    className="pagination-controls-goto-input"
                                                                                    value={currentPage}
                                                                                    onChange={(e) => {
                                                                                        const newPage = Number(e.target.value);
                                                                                        if (newPage >= 1 && newPage <= totalPages) {
                                                                                            setCurrentPage(newPage);
                                                                                        }
                                                                                    }}
                                                                                    onBlur={() => {
                                                                                        if (currentPage < 1) setCurrentPage(1);
                                                                                        if (currentPage > totalPages) setCurrentPage(totalPages);
                                                                                    }}
                                                                                    style={{ textAlign: 'center' }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                    </>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button variant="outline-secondary" className="ms-3" onClick={() => navigate('/onboarding-country-list')}>
                                Cancel
                            </Button>
                            <Button variant="outline-danger" onClick={handleDeleteCountry}>
                                Delete Country
                            </Button>
                            <Button variant="outline-secondary" onClick={handleExportPdf}>
                                Download Country Report
                            </Button>
                        </Col>
                    </Row>
                
                </Container>
            </main>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this country? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="outline-danger" onClick={confirmDeleteCountry}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Saved info Modal */}
            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">Update successful</h2>
                    <p className="text-center">Country General Info Updated</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CountryEditGeneral;
